/**
 * AD_SALES wizard form re-hydration helper.
 *
 * Converts a DetailOpportunity (from GET /opportunity-groups/:id) back into the
 * AdSalesWizardData shape understood by the wizard steps. Used in two places:
 *   1. Back-button re-fetch in CreateAdSales — re-populates form from server state.
 *   2. useDuplicateCampaign AD_SALES branch — keeps the stripping/hydration logic DRY.
 *
 * When adPackages are available, display fields (packageName, packagePrice,
 * durationDays, durationLabel) are resolved from the catalog; when the catalog is
 * empty the values fall back to whatever is already on the raw sel object.
 */
import type { Opportunity as DetailOpportunity } from '../../pages/CampaignDetail/opportunity';
import type {
  AdSalesPackage,
  AdSalesWizardData,
  AdSalesInstance,
  AdSalesAsset,
  LegalEntity,
} from '../../pages/CreateAdSales/mockData';
import { flattenAssets, newInstanceId } from '../../pages/CreateAdSales/mockData';
import { expandBookingSlotsToDays } from '../../utils/scheduleDays';
import { TIMEZONE_MAP } from '../../utils/timezone';
import { fromIsoDatetime } from './wizardMappers';

/**
 * Re-hydrate AdSales wizard form data from a DetailOpportunity.
 *
 * @param source       Normalised detail response (from mapApiDetailToOpportunity)
 * @param adPackages   Package catalog for this brand — used to fill display fields.
 *                     Pass [] to skip catalog lookup (preserves existing fallback behaviour).
 * @param opts.preserveDates  true = edit/resume (keep dates, proof, IO status);
 *                            false = duplicate/resubmit (clear dates + proof).
 */
export function hydrateAdSalesFormData(
  source: DetailOpportunity,
  adPackages: AdSalesPackage[],
  opts: { preserveDates: boolean }
): Partial<AdSalesWizardData> {
  const { preserveDates } = opts;
  // Slot bounds are written at market midnight, so they only decode back to the day the
  // merchant picked when read in the same zone.
  const marketZone = TIMEZONE_MAP[(source.country || '').toUpperCase()];

  // ── Rebuild selected instances ────────────────────────────────
  // When no catalog match, distribute the authoritative price evenly across packages.
  const rawSelected = (source.selected || []) as Array<Record<string, unknown>>;
  const priceFromSummary =
    source.priceSummary && rawSelected.length > 0 ? source.priceSummary.totalPrice / rawSelected.length : 0;

  const selected: AdSalesInstance[] = rawSelected.map((sel) => {
    // Identify catalog package by tier/id so we can fill display fields
    const pkgTier = String(sel.packageTier || sel.packageId || '');
    const catalogPkg = adPackages.find((p) => p.id === pkgTier);

    // ── Expand bookingSlots → assetSchedule ──────────────────
    const existingSchedule = (sel.assetSchedule || {}) as Record<string, Date[]>;
    const assets = (sel.assets ?? []) as Array<Record<string, unknown>>;
    const hydratedSchedule: Record<string, Date[]> = { ...existingSchedule };

    for (const asset of assets) {
      const slots = (asset.bookingSlots ?? []) as Array<{ startTime: string; endTime: string }>;
      const assetName = String(asset.name || '');
      // Only expand if there are slots and no existing schedule for this asset
      if (slots.length > 0 && assetName && !existingSchedule[assetName]?.length) {
        hydratedSchedule[assetName] = expandBookingSlotsToDays(slots, marketZone);
      }
    }

    // ── Resolve asset list ────────────────────────────────────
    // Prefer the assets that came back from the server (they carry bookingSlots).
    // Fall back to flattening the catalog package when there are no server assets.
    const pkgAssets: AdSalesAsset[] =
      assets.length > 0 ? (assets as unknown as AdSalesAsset[]) : catalogPkg ? flattenAssets(catalogPkg) : [];

    return {
      instanceId: String(sel.instanceId || newInstanceId()),
      packageId: pkgTier,
      // Preserve the server-side DB package row ID so PATCH updates rather than creates.
      ...(sel.id ? { serverPackageId: String(sel.id) } : {}),
      // Display fields — fill from catalog when available, fall back to raw values
      packageName: catalogPkg?.name ?? String(sel.packageName || pkgTier),
      packagePrice: catalogPkg?.priceSGD ?? (priceFromSummary || Number(sel.packagePrice ?? 0)),
      durationDays: catalogPkg?.durationDays ?? Number(sel.durationDays ?? 30),
      durationLabel: catalogPkg?.durationLabel ?? String(sel.durationLabel || '30 days'),
      // Read the window back on the market's calendar, not the browser's: `new Date(iso)` on a
      // market-midnight instant lands on the previous day for every browser west of the market.
      startDate: fromIsoDatetime(preserveDates ? (sel.startDate as string) : null, source.country).date,
      endDate: fromIsoDatetime(preserveDates ? (sel.endDate as string) : null, source.country).date,
      acknowledgedSoldOut: Boolean(sel.acknowledgedSoldOut),
      assets: pkgAssets,
      // Duplicate/resubmit clears startDate/endDate by contract; asset date-picks are
      // anchored to the window start, so clear them too. Leaving them pinned to the
      // source's dates blocks re-anchoring — the wizard only re-seeds an EMPTY schedule
      // when the merchant picks the new window start (handleWindowStart isStillDefault).
      assetSchedule: preserveDates ? hydratedSchedule : {},
    };
  });

  const savedSalesforceId = (source.legalEntity as unknown as LegalEntity | null)?.salesforceId ?? null;

  return {
    billingType: source.billingType === 'Network' ? 'Network' : 'Direct',
    legalEntity: source.legalEntity as unknown as AdSalesWizardData['legalEntity'],
    bankAccount: source.bankAccount as unknown as AdSalesWizardData['bankAccount'],
    invoicingSource: savedSalesforceId ? 'brand_default' : ('manual' as const),
    iuSalesforceId: savedSalesforceId,
    merchantComment: preserveDates ? source.merchantComment || '' : '',
    cardDates: {},
    selected,
    // signedIO: preserve on edit/resume, clear on duplicate.
    // NOTE: the Back handler in CreateAdSales overrides this with server-persisted
    // payment.ioAttachments so the signed PDF is never lost after an upload.
    signedIO: preserveDates ? (source.signedIO as AdSalesWizardData['signedIO']) ?? [] : [],
    networkProof: [], // not returned by the server detail endpoint
    ioDownloaded: preserveDates ? !!(source as Record<string, unknown>).ioDownloaded : false,
    // Always clear these — the Back handler restores them from prev state;
    // duplicate flow always starts fresh.
    ioSignedAck: false,
    acceptedTerms: false,
  };
}
